import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2, Crown } from "lucide-react";
import DOMPurify from "dompurify";
import { api } from "../lib/api";

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [e, list] = await Promise.all([api.getEvent(id), api.listEvents()]);
        setEvent(e);
        setAll(list);
      } catch (err) {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="flex items-center justify-center py-24 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (notFound || !event) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-white">Event nicht gefunden</h1>
        <Link to="/events" className="mt-4 inline-block btn-ghost rounded-lg px-4 py-2 text-sm">← Zu allen Events</Link>
      </section>
    );
  }

  const firstBadge = (event.badges || [])[0];
  const others = all.filter((x) => x.id !== event.id).slice(0, 6);

  return (
    <article className="mx-auto max-w-5xl px-4 py-8" data-testid="event-detail-page">
      <Link to="/events" data-testid="event-detail-back" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Zurueck zu allen Events
      </Link>

      {/* Habbo-Stil News-Header */}
      {event.news_header && (
        <div data-testid="event-news-header" className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b]">
          <img src={event.news_header} alt="" className="hb-pixel-img w-full" />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside className="space-y-4">
          <div className={`rounded-2xl border p-5 text-center ${event.is_legendary ? "border-[#ffb84d]/40 bg-gradient-to-br from-[#1a1410] to-[#0f141b]" : "border-white/10 bg-[#0f141b]"}`}>
            {firstBadge ? (
              <div className="flex flex-col items-center gap-2">
                <img src={`https://images.habbo.com/c_images/album1584/${firstBadge.code}.gif`} alt={firstBadge.code} className="hb-pixel-img h-20 w-20" data-testid="event-main-badge" />
                <div className="text-xs font-bold uppercase tracking-wider text-[#ffb84d]">{firstBadge.code}</div>
              </div>
            ) : event.image ? (
              <img src={event.image} alt="" className="hb-pixel-img mx-auto max-h-24" />
            ) : event.is_legendary ? (
              <Crown className="mx-auto h-12 w-12 text-[#ffb84d]" />
            ) : (
              <Calendar className="mx-auto h-10 w-10 text-zinc-500" />
            )}
            {event.is_legendary && (
              <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-[#ffb84d]/40 bg-[#ffb84d]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#ffb84d]">
                <Crown className="h-3 w-3" /> Legendaer
              </div>
            )}
            <div className="mt-3 text-[10px] uppercase tracking-wider text-zinc-500">Jahr</div>
            <div className="pixel-text text-2xl text-white">{event.year || "—"}</div>
          </div>

          {/* Alle Badges */}
          {event.badges && event.badges.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-[#b39bff]">Verspielte Badges</div>
              <div className="grid grid-cols-2 gap-2">
                {event.badges.map((b) => (
                  <div key={b.code} className="flex items-center gap-2 rounded-md border border-white/5 bg-[#10141b] p-2" title={b.description}>
                    <img src={`https://images.habbo.com/c_images/album1584/${b.code}.gif`} alt={b.code} className="hb-pixel-img h-7 w-7 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[11px] font-bold text-white">{b.name || b.code}</div>
                      <div className="truncate text-[10px] text-zinc-500">{b.code}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main */}
        <main>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">{event.is_legendary ? "Legendaeres Event" : "Habbo Event"}</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold text-white">{event.name}</h1>
          {event.short && <p className="mt-3 text-base text-zinc-300">{event.short}</p>}

          {event.description_html && (
            <div
              className="news-content mt-6 max-w-none text-sm leading-7 text-zinc-200"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.description_html) }}
            />
          )}
        </main>
      </div>

      {/* Weitere Events */}
      {others.length > 0 && (
        <section className="mt-12 border-t border-white/10 pt-8">
          <h2 className="text-lg font-bold text-white">Weitere Events</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {others.map((o) => {
              const fb = (o.badges || [])[0];
              return (
                <Link
                  key={o.id}
                  to={`/events/${o.id}`}
                  data-testid={`event-detail-other-${o.id}`}
                  className="card-hover flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-[#0f141b] p-3 text-center hover:border-white/25"
                >
                  {fb ? (
                    <img src={`https://images.habbo.com/c_images/album1584/${fb.code}.gif`} alt={fb.code} className="hb-pixel-img h-12" />
                  ) : o.image ? (
                    <img src={o.image} alt="" className="hb-pixel-img h-12" />
                  ) : o.is_legendary ? (
                    <Crown className="h-8 w-8 text-[#ffb84d]" />
                  ) : (
                    <Calendar className="h-8 w-8 text-zinc-500" />
                  )}
                  <div className="text-xs font-semibold text-white line-clamp-2">{o.name}</div>
                  <div className="text-[10px] text-zinc-500">{o.year}</div>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Link to="/events" className="btn-primary inline-block rounded-lg px-4 py-2 text-sm font-bold">Alle Events ansehen →</Link>
          </div>
        </section>
      )}
    </article>
  );
};

export default EventDetail;
